import { Controller } from "@hotwired/stimulus"

// AI billing-entry generator for time activity descriptions.
//
// Shows itself only when the description contains a Beacon ticket reference
// ("Ticket NVT12345:"). Clicking the button posts to /api/v1/time_activities/ai_message
// and rewrites the description with the AI-generated billing entry, preserving the
// "Ticket NVT…: " prefix.
export default class extends Controller {
  static targets = ["button", "description", "icon"]
  static values = {
    timeActivityId: String,
    url: { type: String, default: "/api/v1/time_activities/ai_message" }
  }

  static TICKET_PATTERN = /Ticket\s+NVT\d+:/

  connect() {
    this.toggle()
  }

  toggle() {
    if (!this.hasButtonTarget || !this.hasDescriptionTarget) return
    const hasTicket = this.constructor.TICKET_PATTERN.test(this.descriptionTarget.value || "")
    this.buttonTarget.hidden = !hasTicket
  }

  async generate() {
    if (this.buttonTarget.disabled) return

    const description = this.descriptionTarget.value
    const dateField = document.getElementById("time_activity_txn_date")
    const date = dateField ? dateField.value : ""

    this.setGenerating(true)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({
          description,
          date,
          time_activity_id: this.timeActivityIdValue
        })
      })

      const data = await response.json()

      if (data.success) {
        const match = description.match(this.constructor.TICKET_PATTERN)
        const prefix = match ? `${match[0].replace(/:$/, "")}: ` : ""
        this.descriptionTarget.value = prefix + (data.text || "")
        this.descriptionTarget.dispatchEvent(new Event("input", { bubbles: true }))
      } else {
        alert(data.error || "Failed to generate message")
      }
    } catch (err) {
      alert(`Error generating message: ${err.message}`)
    } finally {
      this.setGenerating(false)
    }
  }

  setGenerating(generating) {
    const button = this.buttonTarget
    button.disabled = generating
    button.setAttribute("aria-busy", generating ? "true" : "false")

    const idle = (button.dataset.idleClass || "").split(/\s+/).filter(Boolean)
    const busy = (button.dataset.busyClass || "").split(/\s+/).filter(Boolean)

    if (generating) {
      button.classList.remove(...idle)
      button.classList.add(...busy)
    } else {
      button.classList.remove(...busy)
      button.classList.add(...idle)
    }

    if (this.hasIconTarget) {
      this.iconTarget.classList.toggle("animate-ai-twinkle", generating)
    }
  }

  csrfToken() {
    const tag = document.querySelector('meta[name="csrf-token"]')
    return tag ? tag.getAttribute("content") : ""
  }
}
